import { useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'

interface HumanCaptchaModalProps {
  siteKey: string
  actionText?: string
  onBack: () => void
  onRetry?: () => void
  showRetry?: boolean
  errorMessage?: string
  isSubmitting?: boolean
}

export function HumanCaptchaModal({
  siteKey,
  actionText = 'continue',
  onBack,
  onRetry,
  showRetry = false,
  errorMessage,
  isSubmitting = false,
}: HumanCaptchaModalProps) {
  const recaptchaRef = useRef<HTMLDivElement>(null)
  const isRenderedRef = useRef(false)

  useEffect(() => {
    let retryInterval: ReturnType<typeof setInterval> | null = null
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const renderRecaptcha = () => {
      if (isRenderedRef.current) return
      if (!(window as any).grecaptcha) return
      if (!recaptchaRef.current) return

      try {
        (window as any).grecaptcha.render(recaptchaRef.current, {
          sitekey: siteKey,
          callback: 'handleHumanCaptchaComplete',
          theme: 'light',
        })
        isRenderedRef.current = true

        if (retryInterval) {
          clearInterval(retryInterval)
          retryInterval = null
        }
        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
      } catch (error) {
        console.error('Error rendering reCAPTCHA:', error)
      }
    }

    renderRecaptcha()

    if (!isRenderedRef.current) {
      retryInterval = setInterval(renderRecaptcha, 500)
      timeoutId = setTimeout(() => {
        if (retryInterval) {
          clearInterval(retryInterval)
          retryInterval = null
        }
      }, 10000)
    }

    return () => {
      if (retryInterval) clearInterval(retryInterval)
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [siteKey])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-6 text-center">
          <h2 className="text-xl font-bold">Security Check</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Please complete the security check to {actionText}
          </p>
        </div>

        <div className="mb-4 flex justify-center">
          <div ref={recaptchaRef} />
        </div>

        {errorMessage && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-center text-sm text-red-600">{errorMessage}</p>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            onClick={onBack}
            variant="outline"
            className="flex-1"
            disabled={isSubmitting}
          >
            Back
          </Button>

          {showRetry && onRetry && (
            <Button
              onClick={onRetry}
              className="flex-1"
              disabled={isSubmitting}
            >
              Try Again
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
